import * as React from "react"
import LogoTypeIcon from "@/components/LogoTypeIcon"
import {
  Link,
  useTranslation,
  useI18next,
} from "@zestia/gatsby-plugin-react-i18next"
import LanguageSwitcher from "@/components/LanguageSwitcher"
import BlogCategoryNavigation from "@/components/BlogCategoryNavigation"
import NavBarMobileMenu from "@/components/NavBarMobileMenu"
import NavBarMenuFeatures from "./NavBarMenuFeatures"
import NavBarMenuIntegrations from "./NavBarMenuIntegrations"
import NavBarMenuSolutions from "./NavBarMenuSolutions"
import NavBarMenuResources from "./NavBarMenuResources"
import { usePreventScroll } from "react-aria"
import ScrollProgressBar from "@/components/ScrollProgressBar"
import LogInButton from "@/components/LogInButton"
import NavBarKicker from "@/components/NavBarKicker"
import useMenuToggle from "@/hooks/useMenuToggle"
import useIsMobile from "@/hooks/useIsMobile"

const NavBar = ({
  showLogo,
  showNavLinks,
  showLanguageSwitcher,
  showLogIn,
  showSignUp,
  buttonText,
  isSpecialOffer,
  darkNav,
  transparentNav,
  availableSlugs = null,
  className = "",
}) => {
  const { t } = useTranslation()
  const { originalPath } = useI18next()

  const isBlogHomeOrCategoryPage =
    originalPath === "/blog/" || originalPath.match(/^\/blog\/category\//)

  const navBarRef = React.useRef()

  const isMobile = useIsMobile()
  const [isPinned, setIsPinned] = React.useState(false)
  const [isNavigationOpen, setIsNavigationOpen] = React.useState(false)
  const [showMenu, setShowMenu] = React.useState(null)

  const [anyOpenMenu, setAnyOpenMenu] = React.useState(false)
  const toggleNavigation = () => {
    setIsNavigationOpen(prev => !prev)
  }

  const menuTypes = [
    "features",
    "integrations",
    "solutions",
    "resources",
    "language",
  ]

  React.useEffect(() => {
    setAnyOpenMenu(menuTypes.includes(showMenu))
  }, [showMenu])

  const {
    handleToggle: handleMenuToggle,
    menuRef,
    languageRef,
  } = useMenuToggle(setShowMenu)

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => {
        setIsPinned(e.intersectionRatio < 1)
      },
      { threshold: [1] },
    )

    observer.observe(navBarRef.current)

    return () => observer.disconnect()
  }, [])

  usePreventScroll({ isDisabled: !(showMenu && isMobile) })

  const [isTransparent, setIsTransparent] = React.useState(transparentNav)
  const [isDark, setIsDark] = React.useState(darkNav)
  React.useEffect(() => {
    setIsTransparent(transparentNav && !isPinned && !showMenu)
    setIsDark(darkNav && !isPinned && !showMenu)
  }, [isPinned, showMenu])

  // Reset menu state on component mount
  React.useEffect(() => {
    setShowMenu(null)
  }, [])

  const isBlackFridayCampaignPage = originalPath === "/campaign/black-friday/"

  return (
    <>
      {anyOpenMenu && <div className="menu__overlay" />}

      <NavBarKicker isSpecialOffer={isSpecialOffer} />

      <nav
        className={`nav-bar ${
          isNavigationOpen ? "nav-bar--open" : ""
        } ${className}
         ${isPinned ? "nav-bar--pinned" : ""}
         ${isDark ? "nav-bar--dark" : ""}
          ${isTransparent ? "nav-bar--transparent" : ""}
          ${transparentNav ? "nav-bar--adjust" : ""}
         `}
        ref={navBarRef}
      >
        <div className="nav-bar__logo-container">
          {showLogo && (
            <Link className="nav-bar__logo" to="/" aria-label="Capsule CRM">
              <LogoTypeIcon className={isDark ? "logotype--light" : ""} />
            </Link>
          )}
        </div>

        {showNavLinks && (
          <div className="nav-bar__links" ref={menuRef}>
            <div className="language-switcher--mobile">
              {(availableSlugs === null || availableSlugs.length > 1) && (
                <LanguageSwitcher
                  availableSlugs={availableSlugs ?? []}
                  showMenu={showMenu === "language"}
                  setShowMenu={() => handleMenuToggle("language")}
                  light={isDark}
                />
              )}
            </div>

            <NavBarMenuFeatures
              showMenu={showMenu === "features"}
              setShowMenu={() => handleMenuToggle("features")}
            />

            <Link className="nav-bar__btn" to="/signup/">
              {t("navigation.pricing.nav-title")}
            </Link>

            <NavBarMenuIntegrations
              showMenu={showMenu === "integrations"}
              setShowMenu={() => handleMenuToggle("integrations")}
            />

            <NavBarMenuSolutions
              showMenu={showMenu === "solutions"}
              setShowMenu={() => handleMenuToggle("solutions")}
            />

            <NavBarMenuResources
              showMenu={showMenu === "resources"}
              setShowMenu={() => handleMenuToggle("resources")}
            />
          </div>
        )}

        <aside className="nav-bar__aside">
          {showLanguageSwitcher && (
            <div className="language-switcher--desktop" ref={languageRef}>
              {(availableSlugs === null || availableSlugs.length > 1) && (
                <LanguageSwitcher
                  availableSlugs={availableSlugs ?? []}
                  showMenu={showMenu === "language"}
                  setShowMenu={() => handleMenuToggle("language")}
                  light={isDark}
                />
              )}
            </div>
          )}

          {showLogIn && (
            <LogInButton className="nav-bar__btn nav-bar__btn--login btn--login-mobile" />
          )}

          {showSignUp &&
            (isBlackFridayCampaignPage ? (
              <a
                className="btn btn--sm btn--dark btn--signup-mobile"
                href="#pricing-tiers"
              >
                {buttonText}
              </a>
            ) : (
              <Link
                className={`btn btn--sm btn--dark btn--signup-mobile ${
                  isDark ? "btn--white" : ""
                }`}
                to="/signup/"
                title="Try Capsule for free"
                data-variation="FrictionlessHomeAssetV0Bypass"
              >
                {buttonText || t("navigation.sign-up-btn")}
              </Link>
            ))}
        </aside>

        <NavBarMobileMenu onToggle={toggleNavigation} light={isDark} />

        <ScrollProgressBar selectorToTrack=".blog-post__content" />
      </nav>

      {isBlogHomeOrCategoryPage && <BlogCategoryNavigation />}
    </>
  )
}

export default NavBar
